import React from 'react';
import { 
  X, 
  Printer, 
  Share2 
} from 'lucide-react';

export default function InvoiceModal({ invoiceData, currentTenant, onClose, lang = 'mr', selectedMonth }) {
  if (!invoiceData) return null;

  const isMr = lang === 'mr';
  const days = Array.from({ length: 30 }, (_, i) => i + 1);

  const targetMonth = selectedMonth || invoiceData?.month || '2026-10';
  const [yr, mo] = targetMonth.split('-');
  const monthNamesMr = { '01': 'जानेवारी', '02': 'फेब्रुवारी', '03': 'मार्च', '04': 'एप्रिल', '05': 'मे', '06': 'जून', '07': 'जुलै', '08': 'ऑगस्ट', '09': 'सप्टेंबर', '10': 'ऑक्टोबर', '11': 'नोव्हेंबर', '12': 'डिसेंबर' };
  const monthNamesEn = { '01': 'January', '02': 'February', '03': 'March', '04': 'April', '05': 'May', '06': 'June', '07': 'July', '08': 'August', '09': 'September', '10': 'October', '11': 'November', '12': 'December' };
  const monthLabel = isMr ? `${monthNamesMr[mo] || mo} ${yr}` : `${monthNamesEn[mo] || mo} ${yr}`;
  const baseUrl = (typeof window !== 'undefined' && window.location?.origin && window.location.origin.startsWith('http'))
    ? window.location.origin
    : 'https://milkpromitra.esensevvg.online';
  const invoicePublicUrl = `${baseUrl}/#invoice-view?slug=${encodeURIComponent(currentTenant?.slug || '')}&customerId=${encodeURIComponent(invoiceData.customerId)}&month=${encodeURIComponent(targetMonth)}`;

  // Safe invoice ID calculation (handles integer & string IDs)
  const safeCustId = String(invoiceData.customerId || '1').replace(/\D/g, '') || '1';
  const tenantBrand = isMr 
    ? (currentTenant?.brandMarathi || currentTenant?.brand_name_mr || currentTenant?.name || 'समर्थ कृपा डेअरी')
    : (currentTenant?.brandEnglish || currentTenant?.brand_name_en || currentTenant?.name || 'Samarth Krupa Dairy');

  const dairySettings = currentTenant?.dairy_settings || {};
  const fssaiNo = dairySettings.fssai_no || currentTenant?.fssai_no;
  const gstNo = dairySettings.gst_no || currentTenant?.gst_no;
  const printFssaiGst = dairySettings.print_fssai_gst !== '0';

  const printBank = dairySettings.print_bank_details === '1';
  const bankName = dairySettings.bank_name;
  const bankAccountNo = dairySettings.bank_account_no;
  const bankIfsc = dairySettings.bank_ifsc;
  const bankHolderName = dairySettings.bank_holder_name;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 150,
      padding: '1rem'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: 18,
        maxWidth: 640,
        width: '100%',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)',
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Top Control Bar (Hidden on print) */}
        <div className="no-print" style={{
          background: '#0f172a',
          color: '#ffffff',
          padding: '0.85rem 1.25rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>
            {isMr ? 'दूध बिल पावती प्रिव्ह्यू' : 'Milk Bill Invoice Preview'}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => window.print()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.35rem 0.85rem',
                borderRadius: 8,
                background: '#0284c7',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer'
              }}
            >
              <Printer size={14} />
              <span>{isMr ? 'पावती प्रिंट करा' : 'Print Invoice'}</span>
            </button>

            {/* Direct WhatsApp Share Button */}
            <a
              href={`https://wa.me/91${(invoiceData.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent(
                `*🥛 ${tenantBrand} - ${isMr ? 'मासिक दूध बिल पावती' : 'Monthly Milk Bill'} 🥛*\n` +
                `${isMr ? 'ग्राहक:' : 'Customer:'} ${invoiceData.customerName}\n` +
                `${isMr ? 'बिलिंग महिना:' : 'Month:'} ${monthLabel}\n\n` +
                `🥛 ${isMr ? 'एकूण दूध:' : 'Total Milk:'} ${invoiceData.totalMilkLiters} L (${invoiceData.deliveredDays} ${isMr ? 'दिवस' : 'days'})\n` +
                `💵 ${isMr ? 'दूध बिल रक्कम:' : 'Milk Amount:'} ₹${invoiceData.milkAmount}\n` +
                (invoiceData.extraAmount > 0 ? `📦 ${isMr ? 'उप-उत्पादने:' : 'Extra Items:'} ₹${invoiceData.extraAmount}\n` : '') +
                `⏮️ ${isMr ? 'मागील बाकी:' : 'Previous Due:'} ₹${invoiceData.prevBalance}\n` +
                `------------------------------\n` +
                `*₹${invoiceData.totalBill} (${isMr ? 'एकूण देय रक्कम' : 'Total Payable'})*\n` +
                `------------------------------\n\n` +
                `📲 ${isMr ? 'आपली सविस्तर डिजिटल पावती पाहण्यासाठी खालील लिंकवर क्लिक करा:' : 'View detailed digital invoice:'}\n` +
                `${invoicePublicUrl}\n\n` +
                `आपला UPI आयडी / पेमेंट पर्याय: ${currentTenant?.dairy_settings?.upi_id || currentTenant?.upi_id || 'dairy@upi'}\n` +
                `धन्यवाद! 🙏`
              )})}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.35rem 0.85rem',
                borderRadius: 8,
                background: '#16a34a',
                color: '#ffffff',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
            >
              <Share2 size={13} />
              <span>WhatsApp</span>
            </a>
            <button
              onClick={onClose}
              style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Printable Area */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', fontSize: '0.82rem' }}>
          {/* Header */}
          <div style={{ borderBottom: '2px solid #0f172a', paddingBottom: '0.85rem', display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase' }}>
                {isMr ? '॥ दूध वितरण बिल पावती ॥' : '॥ MILK DELIVERY BILL INVOICE ॥'}
              </div>
              <h2 style={{ fontSize: '1.45rem', fontWeight: 900, color: '#0f172a', margin: '3px 0 2px 0' }}>
                {tenantBrand}
              </h2>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                {isMr ? 'डेअरी प्रॉडक्ट्स संकलन व वितरण' : 'Dairy Products Collection & Distribution'}
              </div>
              <div style={{ fontSize: '0.72rem', color: '#475569', marginTop: 2 }}>
                {isMr ? 'मोबाईल:' : 'Phone:'} {dairySettings.phone || currentTenant?.phone || '9822012345'} • UPI: {dairySettings.upi_id || currentTenant?.upi_id || currentTenant?.upiId || 'dairy@upi'}
              </div>
              {printFssaiGst && (fssaiNo || gstNo) && (
                <div style={{ fontSize: '0.70rem', color: '#475569', marginTop: 2, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {fssaiNo && <span><strong>FSSAI:</strong> {fssaiNo}</span>}
                  {fssaiNo && gstNo && <span>•</span>}
                  {gstNo && <span><strong>GSTIN:</strong> {gstNo}</span>}
                </div>
              )}
            </div>

            <div style={{ textAlign: 'right' }}>
              <span style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: 6, fontWeight: 800, fontSize: '0.75rem', fontFamily: 'monospace' }}>
                #MPM-2026-09-{safeCustId.padStart(4, '0')}
              </span>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 6 }}>
                {isMr ? 'महिना:' : 'Month:'} <strong>{isMr ? 'सप्टेंबर २०२६' : 'September 2026'}</strong>
              </div>
              <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                {isMr ? 'दिनांक: ३०/०९/२०२६' : 'Date: 30/09/2026'}
              </div>
            </div>
          </div>

          {/* Customer info */}
          <div style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                {isMr ? 'ग्राहक माहिती' : 'Customer Details'}
              </div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', marginTop: 2 }}>{invoiceData.customerName}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{invoiceData.address}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{isMr ? 'फोन:' : 'Phone:'} {invoiceData.phone}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>
                {isMr ? 'वितरण तपशील' : 'Delivery Summary'}
              </div>
              <div style={{ fontWeight: 700, color: '#0f172a', marginTop: 2 }}>
                {isMr ? 'एकूण लिटर:' : 'Total Litres:'} {invoiceData.totalMilkLiters} L
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                {isMr ? 'वितरित दिवस:' : 'Delivered Days:'} {invoiceData.deliveredDays} {isMr ? 'दिवस' : 'days'}
              </div>
            </div>
          </div>

          {/* 30-Day Matrix */}
          <div style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.5rem' }}>
              {isMr ? 'दैनिक वितरण कॅलेंडर हजेरी:' : 'Daily Delivery Calendar Attendance:'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 3, textAlign: 'center', fontSize: '0.65rem' }}>
              {days.map(d => {
                const isAbsent = invoiceData.deliveredDays < 25 && (d > 10 && d < 22);
                return (
                  <div
                    key={d}
                    style={{
                      background: isAbsent ? '#ffe4e6' : '#dcfce7',
                      color: isAbsent ? '#e11d48' : '#15803d',
                      border: '1px solid ' + (isAbsent ? '#fecdd3' : '#bbf7d0'),
                      borderRadius: 4,
                      padding: '2px 0'
                    }}
                  >
                    <div style={{ color: '#94a3b8', fontSize: '0.55rem' }}>{d}</div>
                    <div style={{ fontWeight: 800 }}>{isAbsent ? '✕' : (invoiceData.totalMilkLiters / (invoiceData.deliveredDays || 30)).toFixed(1)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Itemized Table */}
          <div style={{ padding: '0.85rem 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #0f172a', fontWeight: 800 }}>
                  <th style={{ textAlign: 'left', paddingBottom: 6 }}>{isMr ? 'तपशील' : 'Description'}</th>
                  <th style={{ textAlign: 'center', paddingBottom: 6 }}>{isMr ? 'प्रमाण' : 'Quantity'}</th>
                  <th style={{ textAlign: 'right', paddingBottom: 6 }}>{isMr ? 'रक्कम' : 'Amount'}</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 0' }}>{isMr ? `दूध वितरण (${invoiceData.deliveredDays} दिवस)` : `Milk Delivery (${invoiceData.deliveredDays} days)`}</td>
                  <td style={{ textAlign: 'center', padding: '6px 0' }}>{invoiceData.totalMilkLiters} L</td>
                  <td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 700 }}>₹{invoiceData.milkAmount}</td>
                </tr>
                {invoiceData.extraAmount > 0 && (
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 0' }}>{isMr ? 'अतिरिक्त उत्पादने (पनीर / तूप)' : 'Dairy Byproducts'}</td>
                    <td style={{ textAlign: 'center', padding: '6px 0' }}>-</td>
                    <td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 700, color: '#059669' }}>+₹{invoiceData.extraAmount}</td>
                  </tr>
                )}
                {invoiceData.prevBalance > 0 && (
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 0' }}>{isMr ? 'मागील शिल्लक बाकी' : 'Previous Due'}</td>
                    <td style={{ textAlign: 'center', padding: '6px 0' }}>-</td>
                    <td style={{ textAlign: 'right', padding: '6px 0', fontWeight: 700, color: '#e11d48' }}>+₹{invoiceData.prevBalance}</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #0f172a', fontWeight: 900, fontSize: '0.95rem' }}>
                  <td style={{ paddingTop: 8 }}>{isMr ? 'एकूण देय रक्कम' : 'Total Payable Amount'}</td>
                  <td style={{ textAlign: 'center', paddingTop: 8 }}>-</td>
                  <td style={{ textAlign: 'right', paddingTop: 8, color: '#0284c7' }}>₹{invoiceData.totalBill}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Payment & Sign */}
          <div style={{ marginTop: '0.5rem', background: '#f8fafc', padding: '0.85rem', borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', border: '1px solid #e2e8f0' }}>
            <div style={{ maxWidth: '65%' }}>
              <div style={{ fontWeight: 800, fontSize: '0.78rem', color: '#0f172a' }}>{isMr ? 'UPI द्वारे बिल भरा:' : 'Pay bill via UPI:'}</div>
              <div style={{ fontFamily: 'monospace', color: '#0284c7', fontWeight: 700, marginTop: 2, fontSize: '0.88rem' }}>
                {dairySettings.upi_id || currentTenant?.upi_id || currentTenant?.upiId || 'dairy@upi'}
              </div>

              {printBank && bankAccountNo && (
                <div style={{ marginTop: '0.65rem', paddingTop: '0.5rem', borderTop: '1px dashed #cbd5e1', fontSize: '0.72rem', color: '#334155' }}>
                  <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>
                    🏦 {isMr ? 'बँक खाते तपशील (NEFT / IMPS):' : 'Bank Account Details:'}
                  </div>
                  {bankName && <div><strong>{isMr ? 'बँक:' : 'Bank:'}</strong> {bankName}</div>}
                  <div><strong>{isMr ? 'खाते क्र.:' : 'A/C No:'}</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{bankAccountNo}</span></div>
                  {bankIfsc && <div><strong>IFSC:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{bankIfsc}</span></div>}
                  {bankHolderName && <div><strong>{isMr ? 'खातेदार:' : 'Name:'}</strong> {bankHolderName}</div>}
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right', minWidth: 120 }}>
              <div style={{ height: 35 }}></div>
              <div style={{ borderTop: '1px solid #94a3b8', fontSize: '0.72rem', fontWeight: 700, paddingTop: 3 }}>
                {isMr ? 'स्वाक्षरी / डेअरी' : 'Authorized Signature'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
